import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

const AboutPage = () => {
  const values = [
    {
      title: "Innovation",
      description: "Pushing boundaries while respecting tradition, we create architecture that defines the future.",
    },
    {
      title: "Sustainability",
      description: "Every design considers its environmental impact, using materials and methods that preserve our planet.",
    },
    {
      title: "Collaboration",
      description: "We work closely with clients, communities, and craftsmen to bring visions to life.",
    },
    {
      title: "Excellence",
      description: "Attention to detail and pursuit of perfection guide every line we draw.",
    },
  ];

  const timeline = [
    { year: "1999", event: "IIDAS founded in Bangalore by Arjun Mehta" },
    { year: "2005", event: "First international recognition at Venice Biennale" },
    { year: "2012", event: "Expanded to Mumbai and Delhi offices" },
    { year: "2018", event: "Launched sustainable design initiative" },
    { year: "2024", event: "150+ projects completed across India" },
  ];

  return (
    <div data-testid="about-page" className="bg-[#F4F4F0] pt-24">
      {/* Hero Section */}
      <section className="px-6 md:px-12 lg:px-24 py-24 md:py-32">
        <div className="max-w-4xl">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-xs uppercase tracking-[0.2em] text-[#1A1A1A]/60 mb-6"
          >
            About Us
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="font-heading text-5xl sm:text-6xl lg:text-7xl font-light tracking-tight text-[#1A1A1A] leading-[0.9] mb-8"
          >
            Crafting spaces
            <br />
            that tell stories.
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-lg md:text-xl font-light text-[#1A1A1A]/70 leading-relaxed max-w-2xl"
          >
            IIDAS is an award-winning architecture firm dedicated to creating meaningful spaces that resonate with people and place.
          </motion.p>
        </div>
      </section>

      {/* Image Section */}
      <section className="px-6 md:px-12 lg:px-24 pb-24">
        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          className="w-full aspect-[21/9] overflow-hidden"
        >
          <img
            src="https://images.unsplash.com/photo-1761877676992-0c232a7920f2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwxfHxjb25zdHJ1Y3Rpb24lMjBzaXRlJTIwbW9kZXJuJTIwYnVpbGRpbmclMjBmcmFtZXxlbnwwfHx8fDE3NzIyNjA0ODd8MA&ixlib=rb-4.1.0&q=85"
            alt="Modern construction"
            className="w-full h-full object-cover"
          />
        </motion.div>
      </section>

      {/* Philosophy Section */}
      <section className="px-6 md:px-12 lg:px-24 py-24 md:py-32 bg-[#E5E5E0]">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-16 md:gap-24">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-[#1A1A1A]/60 mb-6">
              Our Philosophy
            </p>
            <h2 className="font-heading text-4xl md:text-5xl font-light text-[#1A1A1A] mb-8">
              Design with purpose,
              <br />
              build with passion.
            </h2>
          </div>
          <div className="space-y-6">
            <p className="text-lg font-light text-[#1A1A1A]/70 leading-relaxed">
              At IIDAS, we believe that architecture is more than creating buildings—it's about shaping experiences. Our approach combines deep respect for context with innovative design thinking.
            </p>
            <p className="text-lg font-light text-[#1A1A1A]/70 leading-relaxed">
              Every project begins with understanding: the site, the climate, the culture, and most importantly, the people who will inhabit the space. This human-centered approach ensures that our designs are not just beautiful, but truly meaningful.
            </p>
            <p className="text-lg font-light text-[#1A1A1A]/70 leading-relaxed">
              We draw inspiration from India's rich architectural heritage while embracing contemporary technologies and sustainable practices.
            </p>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section
        data-testid="values-section"
        className="px-6 md:px-12 lg:px-24 py-24 md:py-32"
      >
        <div className="max-w-2xl mb-16">
          <p className="text-xs uppercase tracking-[0.2em] text-[#1A1A1A]/60 mb-4">
            Our Values
          </p>
          <h2 className="font-heading text-4xl md:text-5xl font-light text-[#1A1A1A]">
            Principles that guide us
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {values.map((value, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="border-t border-[#1A1A1A]/20 pt-8"
              data-testid={`value-card-${index}`}
            >
              <span className="text-xs text-[#3A4D39] font-medium">
                {String(index + 1).padStart(2, "0")}
              </span>
              <h3 className="font-heading text-2xl text-[#1A1A1A] mt-4 mb-3">
                {value.title}
              </h3>
              <p className="text-sm text-[#1A1A1A]/60 leading-relaxed">
                {value.description}
              </p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Timeline Section */}
      <section
        data-testid="timeline-section"
        className="px-6 md:px-12 lg:px-24 py-24 md:py-32 bg-[#1A1A1A] text-[#F4F4F0]"
      >
        <div className="max-w-2xl mb-16">
          <p className="text-xs uppercase tracking-[0.2em] text-[#F4F4F0]/50 mb-4">
            Our Journey
          </p>
          <h2 className="font-heading text-4xl md:text-5xl font-light">
            25 years of excellence
          </h2>
        </div>

        <div className="space-y-0">
          {timeline.map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="border-b border-[#F4F4F0]/10 py-8 grid grid-cols-12 gap-4 items-center"
              data-testid={`timeline-item-${index}`}
            >
              <span className="col-span-2 md:col-span-1 font-heading text-2xl md:text-3xl text-[#C2B280]">
                {item.year}
              </span>
              <p className="col-span-10 md:col-span-11 text-lg text-[#F4F4F0]/70">
                {item.event}
              </p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="px-6 md:px-12 lg:px-24 py-24 md:py-32">
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="font-heading text-4xl md:text-5xl font-light text-[#1A1A1A] mb-6">
            Ready to start your project?
          </h2>
          <p className="text-lg font-light text-[#1A1A1A]/70 mb-10">
            Let's discuss how we can bring your vision to life.
          </p>
          <Link
            to="/contact"
            data-testid="about-cta-btn"
            className="inline-flex items-center gap-3 bg-[#1A1A1A] text-[#F4F4F0] px-8 py-4 uppercase tracking-widest text-xs hover:bg-[#3A4D39] transition-colors duration-300"
          >
            Get in Touch
            <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
